import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calculator, Save, FolderOpen, Trash2, Layers, Paintbrush,
  Box, TreePine, Grid3x3, Home as HomeIcon, Hammer, Footprints,
} from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const fmt = (n, d = 2) => Number.isFinite(n) ? n.toFixed(d).replace(/\.?0+$/, '') : '0';
const intCeil = (n) => Number.isFinite(n) ? Math.ceil(n) : 0;

// === Individual calculators (pure components) ===

function DrywallCalc() {
  const [area, setArea] = useState('');
  const [sheetSize, setSheetSize] = useState('32'); // sq ft per 4x8 sheet
  const [waste, setWaste] = useState(10);
  const sheets = useMemo(() => {
    const a = parseFloat(area) || 0;
    const s = parseFloat(sheetSize) || 32;
    const w = parseFloat(waste) || 0;
    return intCeil((a / s) * (1 + w / 100));
  }, [area, sheetSize, waste]);
  return (
    <div className="space-y-3">
      <Field label="Total wall/ceiling area (sq ft)" value={area} onChange={setArea} type="number" />
      <Field label="Sheet size (sq ft)" value={sheetSize} onChange={setSheetSize} type="number" hint="4×8=32, 4×12=48" />
      <Field label="Waste % (5–15 typical)" value={waste} onChange={setWaste} type="number" />
      <Result label="Sheets needed" value={sheets} unit="sheets" />
    </div>
  );
}

function PaintCalc() {
  const [area, setArea] = useState('');
  const [coats, setCoats] = useState(2);
  const [coverage, setCoverage] = useState(350);
  const gallons = useMemo(() => {
    const a = parseFloat(area) || 0;
    const c = parseFloat(coats) || 1;
    const cov = parseFloat(coverage) || 350;
    return (a * c) / cov;
  }, [area, coats, coverage]);
  return (
    <div className="space-y-3">
      <Field label="Total area to paint (sq ft)" value={area} onChange={setArea} type="number" />
      <Field label="Coats" value={coats} onChange={setCoats} type="number" />
      <Field label="Coverage per gallon (sq ft)" value={coverage} onChange={setCoverage} type="number" hint="350 typical for walls" />
      <Result label="Paint needed" value={fmt(gallons)} unit={`gallons (${intCeil(gallons)} cans)`} />
    </div>
  );
}

function ConcreteCalc() {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState('');
  const [unit, setUnit] = useState('in'); // depth unit
  const cubicYards = useMemo(() => {
    const L = parseFloat(length) || 0;
    const W = parseFloat(width) || 0;
    let D = parseFloat(depth) || 0;
    if (unit === 'in') D = D / 12; // convert inches → feet
    const cubicFeet = L * W * D;
    return cubicFeet / 27;
  }, [length, width, depth, unit]);
  return (
    <div className="space-y-3">
      <Field label="Length (ft)" value={length} onChange={setLength} type="number" />
      <Field label="Width (ft)" value={width} onChange={setWidth} type="number" />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Depth" value={depth} onChange={setDepth} type="number" />
        <div>
          <label className="text-xs font-bold text-gray-600">Depth unit</label>
          <select value={unit} onChange={(e) => setUnit(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
            <option value="in">inches</option>
            <option value="ft">feet</option>
          </select>
        </div>
      </div>
      <Result label="Concrete needed" value={fmt(cubicYards)} unit="cubic yards" />
      <p className="text-xs text-gray-500">Add ~10% for spillage and overbuilds.</p>
    </div>
  );
}

function LumberCalc() {
  // Stud calc: wall length, on-center spacing → number of studs (+ top/bottom plates)
  const [wallLength, setWallLength] = useState('');
  const [spacing, setSpacing] = useState(16);
  const [studHeight, setStudHeight] = useState(96);
  const calc = useMemo(() => {
    const wl = parseFloat(wallLength) || 0; // ft
    const s = parseFloat(spacing) || 16; // inches OC
    const sh = parseFloat(studHeight) || 96; // inches
    const studs = intCeil((wl * 12) / s) + 1 + 2; // +1 to close, +2 corners
    const plateLF = wl * 3; // top + double-top + bottom = ~3× wall length
    const boardFeet = (studs * sh) / 144; // approx for 2x4
    return { studs, plateLF: fmt(plateLF, 1), boardFeet: fmt(boardFeet, 1) };
  }, [wallLength, spacing, studHeight]);
  return (
    <div className="space-y-3">
      <Field label="Wall length (ft)" value={wallLength} onChange={setWallLength} type="number" />
      <Field label="On-center spacing (in)" value={spacing} onChange={setSpacing} type="number" hint="16 or 24 typical" />
      <Field label="Stud height (in)" value={studHeight} onChange={setStudHeight} type="number" hint="96 = 8ft, 108 = 9ft" />
      <Result label="Studs" value={calc.studs} unit="pieces" />
      <Result label="Plate stock" value={calc.plateLF} unit="linear ft" />
      <Result label="Approx board feet" value={calc.boardFeet} unit="bf" />
    </div>
  );
}

function TileCalc() {
  const [area, setArea] = useState('');
  const [tileSize, setTileSize] = useState(12); // inches per side, square
  const [waste, setWaste] = useState(10);
  const calc = useMemo(() => {
    const a = parseFloat(area) || 0;
    const t = parseFloat(tileSize) || 12;
    const w = parseFloat(waste) || 0;
    const sqInPerTile = t * t;
    const tilesPerSqFt = 144 / sqInPerTile;
    const tiles = intCeil(a * tilesPerSqFt * (1 + w / 100));
    const grout = a / 100; // very rough — bags per 100 sq ft of tile
    return { tiles, groutBags: fmt(grout, 1) };
  }, [area, tileSize, waste]);
  return (
    <div className="space-y-3">
      <Field label="Floor/wall area (sq ft)" value={area} onChange={setArea} type="number" />
      <Field label="Tile size (in, square)" value={tileSize} onChange={setTileSize} type="number" hint="12, 6, 24…" />
      <Field label="Waste %" value={waste} onChange={setWaste} type="number" hint="10 standard, 15 for diagonal" />
      <Result label="Tiles needed" value={calc.tiles} unit="tiles" />
      <Result label="Grout (rough)" value={calc.groutBags} unit="bags" />
    </div>
  );
}

function ShingleCalc() {
  const [area, setArea] = useState('');
  const [bundlesPerSquare, setBundlesPerSquare] = useState(3);
  const [waste, setWaste] = useState(10);
  const calc = useMemo(() => {
    const a = parseFloat(area) || 0;
    const bps = parseFloat(bundlesPerSquare) || 3;
    const w = parseFloat(waste) || 0;
    const squares = (a / 100) * (1 + w / 100);
    return { squares: fmt(squares, 1), bundles: intCeil(squares * bps) };
  }, [area, bundlesPerSquare, waste]);
  return (
    <div className="space-y-3">
      <Field label="Roof area (sq ft, slope-adjusted)" value={area} onChange={setArea} type="number" />
      <Field label="Bundles per square" value={bundlesPerSquare} onChange={setBundlesPerSquare} type="number" hint="3 typical" />
      <Field label="Waste %" value={waste} onChange={setWaste} type="number" />
      <Result label="Squares" value={calc.squares} unit="squares (100 sq ft each)" />
      <Result label="Bundles" value={calc.bundles} unit="bundles" />
    </div>
  );
}

function DeckCalc() {
  const [length, setLength] = useState('');        // ft (along beams)
  const [width, setWidth] = useState('');          // ft (joist span)
  const [joistSpacing, setJoistSpacing] = useState(16); // inches OC
  const [beamRows, setBeamRows] = useState(2);     // typical: 2 (ledger + outer beam, OR 2 beams)
  const [postSpacing, setPostSpacing] = useState(8); // ft OC

  // Lumber sizes
  const [postSize, setPostSize] = useState('6x6');           // 4x4, 6x6
  const [beamSize, setBeamSize] = useState('2x10');          // 2x8, 2x10, 2x12
  const [beamPlies, setBeamPlies] = useState(2);             // single, 2-ply built-up, 3-ply
  const [joistSize, setJoistSize] = useState('2x8');         // 2x6, 2x8, 2x10, 2x12
  const [deckBoardSize, setDeckBoardSize] = useState('5/4x6'); // preset key

  // Deck board sizing
  const DECK_BOARDS = {
    '5/4x6':       { label: '5/4" × 6" (PT/Cedar)',  face: 5.5 },
    '2x6':         { label: '2" × 6"',               face: 5.5 },
    '2x4':         { label: '2" × 4"',               face: 3.5 },
    'comp_1x6':    { label: 'Composite 1" × 6"',     face: 5.5 },
    'comp_1x4':    { label: 'Composite 1" × 4"',     face: 3.5 },
    '5/4x4':       { label: '5/4" × 4"',             face: 3.5 },
    'custom':      { label: 'Custom (set width)',    face: 5.5 },
  };
  const [customBoardFace, setCustomBoardFace] = useState(5.5);
  const boardFaceWidth = deckBoardSize === 'custom'
    ? (parseFloat(customBoardFace) || 5.5)
    : DECK_BOARDS[deckBoardSize].face;

  const [boardGap, setBoardGap] = useState(0.1875); // inches (3/16 typical)
  const [boardWaste, setBoardWaste] = useState(10); // %
  const [boardStockLen, setBoardStockLen] = useState(16); // ft stock length

  // Railing
  const [includeRailing, setIncludeRailing] = useState(true);
  const [railingSides, setRailingSides] = useState(3); // 3 open sides typical
  const [railingPostSpacing, setRailingPostSpacing] = useState(6); // ft
  const [balusterGap, setBalusterGap] = useState(4); // inches
  const [railingPostSize, setRailingPostSize] = useState('4x4'); // 4x4, 6x6
  const [railSize, setRailSize] = useState('2x4'); // 2x4 typical top/bottom rail
  const [balusterSize, setBalusterSize] = useState('2x2'); // 2x2 typical

  const calc = useMemo(() => {
    const L = parseFloat(length) || 0;
    const W = parseFloat(width) || 0;
    const jSp = parseFloat(joistSpacing) || 16;
    const bRows = parseInt(beamRows) || 2;
    const bPlies = parseInt(beamPlies) || 2;
    const pSp = parseFloat(postSpacing) || 8;
    const bw = boardFaceWidth;
    const bg = parseFloat(boardGap) || 0;
    const waste = parseFloat(boardWaste) || 0;
    const stockLen = parseFloat(boardStockLen) || 16;

    if (!L || !W) return null;

    // Posts (footings): per beam row, posts = ceil(L/postSpacing)+1
    const postsPerRow = intCeil(L / pSp) + 1;
    const totalPosts = postsPerRow * bRows;

    // Beams: each row = L linear feet, multiplied by plies.
    const beamLFPerRow = L;
    const totalBeamLF = beamLFPerRow * bRows * bPlies;

    // Joists: number across the length at spacing OC + 1
    const joists = intCeil((L * 12) / jSp) + 1;
    const joistLF = joists * W;

    // Rim/band joists: full perimeter approximation = 2(L+W)
    const rimLF = 2 * (L + W);

    // Deck boards (run perpendicular to joists, length = L ft each)
    const boardRows = intCeil((W * 12) / (bw + bg));
    const deckBoardLF = boardRows * L * (1 + waste / 100);
    const boardsAtStock = intCeil(deckBoardLF / stockLen);

    // Railing
    let railPerimeter = 0, railPosts = 0, railTopBottomLF = 0, balusters = 0;
    if (includeRailing) {
      const sides = parseInt(railingSides) || 3;
      if (sides === 1) railPerimeter = L;
      else if (sides === 2) railPerimeter = L + W;
      else if (sides === 3) railPerimeter = L + 2 * W;
      else railPerimeter = 2 * (L + W);
      const rSp = parseFloat(railingPostSpacing) || 6;
      const bgap = parseFloat(balusterGap) || 4;
      railPosts = intCeil(railPerimeter / rSp) + 1;
      railTopBottomLF = railPerimeter * 2; // top + bottom rail
      const balusterWidth = balusterSize === '2x2' ? 1.5 : (balusterSize === '1x2' ? 1.5 : 0.75);
      balusters = intCeil((railPerimeter * 12) / (bgap + balusterWidth));
    }

    // Fasteners
    const intersections = boardRows * joists;
    const deckScrews = intersections * 2;
    const screwLbs = fmt(deckScrews / 350, 1);

    const joistHangers = joists * 2;
    const postBases = totalPosts;
    const concreteCY = fmt(totalPosts * 0.12, 2);

    return {
      totalPosts,
      postsPerRow,
      totalBeamLF: fmt(totalBeamLF, 1),
      beamLFPerRow: fmt(beamLFPerRow, 1),
      joists,
      joistLF: fmt(joistLF, 1),
      rimLF: fmt(rimLF, 1),
      boardRows,
      deckBoardLF: fmt(deckBoardLF, 1),
      boardsAtStock,
      railPerimeter: fmt(railPerimeter, 1),
      railPosts,
      railTopBottomLF: fmt(railTopBottomLF, 1),
      balusters,
      deckScrews,
      screwLbs,
      joistHangers,
      postBases,
      concreteCY,
    };
  }, [length, width, joistSpacing, beamRows, beamPlies, postSpacing, boardFaceWidth, boardGap, boardWaste, boardStockLen, includeRailing, railingSides, railingPostSpacing, balusterGap, balusterSize]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Deck length (ft)" value={length} onChange={setLength} type="number" />
        <Field label="Deck width (ft)" value={width} onChange={setWidth} type="number" hint="joist span direction" />
      </div>

      <p className="text-[11px] font-bold text-gray-700 mt-3 mb-1">Posts</p>
      <div className="grid grid-cols-2 gap-2">
        <Select label="Post size" value={postSize} onChange={setPostSize} options={[
          { v: '4x4', t: '4×4' },
          { v: '6x6', t: '6×6' },
        ]} />
        <Field label="Post spacing (ft)" value={postSpacing} onChange={setPostSpacing} type="number" hint="8 ft typ. max" />
      </div>

      <p className="text-[11px] font-bold text-gray-700 mt-3 mb-1">Beams</p>
      <div className="grid grid-cols-3 gap-2">
        <Select label="Beam size" value={beamSize} onChange={setBeamSize} options={[
          { v: '2x8',  t: '2×8' },
          { v: '2x10', t: '2×10' },
          { v: '2x12', t: '2×12' },
        ]} />
        <Select label="Plies" value={beamPlies} onChange={(v) => setBeamPlies(parseInt(v))} options={[
          { v: 1, t: '1-ply' },
          { v: 2, t: '2-ply' },
          { v: 3, t: '3-ply' },
        ]} />
        <Field label="Beam rows" value={beamRows} onChange={setBeamRows} type="number" hint="2 typ." />
      </div>

      <p className="text-[11px] font-bold text-gray-700 mt-3 mb-1">Joists</p>
      <div className="grid grid-cols-2 gap-2">
        <Select label="Joist size" value={joistSize} onChange={setJoistSize} options={[
          { v: '2x6',  t: '2×6' },
          { v: '2x8',  t: '2×8' },
          { v: '2x10', t: '2×10' },
          { v: '2x12', t: '2×12' },
        ]} />
        <Field label="Joist spacing (in OC)" value={joistSpacing} onChange={setJoistSpacing} type="number" hint="12 / 16 / 24" />
      </div>

      <p className="text-[11px] font-bold text-gray-700 mt-3 mb-1">Deck boards</p>
      <Select label="Deck board size" value={deckBoardSize} onChange={setDeckBoardSize} options={
        Object.entries(DECK_BOARDS).map(([k, v]) => ({ v: k, t: v.label }))
      } />
      {deckBoardSize === 'custom' && (
        <Field label="Custom face width (in)" value={customBoardFace} onChange={setCustomBoardFace} type="number" />
      )}
      <div className="grid grid-cols-3 gap-2">
        <Field label="Gap (in)" value={boardGap} onChange={setBoardGap} type="number" hint="3/16 = 0.1875" />
        <Field label="Waste %" value={boardWaste} onChange={setBoardWaste} type="number" hint="10–15" />
        <Select label="Stock length (ft)" value={boardStockLen} onChange={(v) => setBoardStockLen(parseFloat(v))} options={[
          { v: 8,  t: '8 ft' },
          { v: 10, t: '10 ft' },
          { v: 12, t: '12 ft' },
          { v: 14, t: '14 ft' },
          { v: 16, t: '16 ft' },
          { v: 20, t: '20 ft' },
        ]} />
      </div>

      <div className="flex items-center gap-2 mt-3">
        <input id="deck-railing" type="checkbox" checked={includeRailing} onChange={(e) => setIncludeRailing(e.target.checked)} className="w-4 h-4" />
        <label htmlFor="deck-railing" className="text-xs font-bold text-gray-700">Include railing</label>
      </div>
      {includeRailing && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Select label="Rail post size" value={railingPostSize} onChange={setRailingPostSize} options={[
              { v: '4x4', t: '4×4' },
              { v: '6x6', t: '6×6' },
            ]} />
            <Select label="Top/bottom rail" value={railSize} onChange={setRailSize} options={[
              { v: '2x4', t: '2×4' },
              { v: '2x6', t: '2×6' },
              { v: '5/4x6', t: '5/4×6' },
            ]} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select label="Baluster" value={balusterSize} onChange={setBalusterSize} options={[
              { v: '2x2',     t: '2×2 wood' },
              { v: '1x2',     t: '1×2 wood' },
              { v: 'metal',   t: '3/4" metal' },
            ]} />
            <Field label="Baluster gap (in)" value={balusterGap} onChange={setBalusterGap} type="number" hint="4 max (code)" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select label="Railing sides" value={railingSides} onChange={setRailingSides} options={[
              { v: '1', t: '1 side (length)' },
              { v: '2', t: '2 sides (L + W)' },
              { v: '3', t: '3 sides (L + 2W)' },
              { v: '4', t: '4 sides (full)' },
            ]} />
            <Field label="Post spacing (ft)" value={railingPostSpacing} onChange={setRailingPostSpacing} type="number" hint="6 ft typ." />
          </div>
        </div>
      )}

      {calc && (
        <div className="space-y-1.5 mt-3">
          <p className="text-[11px] font-bold text-blue-900 mb-1">Posts &amp; Footings</p>
          <Result label={`${postSize} posts`} value={calc.totalPosts} unit={`pcs (${calc.postsPerRow}/row)`} />
          <Result label="Post bases / saddles" value={calc.postBases} unit="pcs" />
          <Result label="Concrete for footings" value={calc.concreteCY} unit="cu yd" />

          <p className="text-[11px] font-bold text-blue-900 mb-1 mt-2">Beams</p>
          <Result label={`${beamSize} beam stock`} value={calc.totalBeamLF} unit={`LF (${beamRows}× row × ${beamPlies}-ply)`} />

          <p className="text-[11px] font-bold text-blue-900 mb-1 mt-2">Joists</p>
          <Result label={`${joistSize} joists`} value={calc.joists} unit="pcs" />
          <Result label="Joist material" value={calc.joistLF} unit="linear ft" />
          <Result label={`${joistSize} rim / band`} value={calc.rimLF} unit="linear ft" />
          <Result label="Joist hangers" value={calc.joistHangers} unit="pcs" />

          <p className="text-[11px] font-bold text-blue-900 mb-1 mt-2">Decking</p>
          <Result
            label={`${deckBoardSize === 'custom' ? `${boardFaceWidth}" face` : DECK_BOARDS[deckBoardSize].label} boards`}
            value={calc.boardsAtStock}
            unit={`pcs @ ${boardStockLen} ft (${calc.boardRows} rows)`}
          />
          <Result label="Total decking material" value={calc.deckBoardLF} unit="linear ft (w/ waste)" />
          <Result label="Deck screws" value={calc.deckScrews} unit={`pcs (~${calc.screwLbs} lb)`} />

          {includeRailing && (
            <>
              <p className="text-[11px] font-bold text-blue-900 mb-1 mt-2">Railing</p>
              <Result label="Railing perimeter" value={calc.railPerimeter} unit="linear ft" />
              <Result label={`${railingPostSize} rail posts`} value={calc.railPosts} unit="pcs" />
              <Result label={`${railSize} top + bottom rail`} value={calc.railTopBottomLF} unit="linear ft" />
              <Result label={`${balusterSize} balusters`} value={calc.balusters} unit="pcs" />
            </>
          )}
        </div>
      )}

      <p className="text-[10px] text-gray-500 mt-2">
        Estimates based on common framing rules. Verify spans &amp; sizing against your local code and lumber species.
      </p>
    </div>
  );
}
function CutListCalc({ projects }) {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [openings, setOpenings] = useState([{ type: 'window', w: '', h: '' }]);
  const [spacing, setSpacing] = useState(16);
  const [includeCeiling, setIncludeCeiling] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [savedLists, setSavedLists] = useState([]);
  const [showSaved, setShowSaved] = useState(false);

  const fetchSaved = async () => {
    try {
      const { data } = await axios.get(`${API}/cut-lists`);
      setSavedLists(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };
  useEffect(() => { fetchSaved(); }, []);

  const totals = useMemo(() => {
    const L = parseFloat(length) || 0;
    const W = parseFloat(width) || 0;
    const H = parseFloat(height) || 0;
    const s = parseFloat(spacing) || 16;
    if (!L || !W || !H) return null;

    // Wall area (all 4 walls + ceiling option) minus openings
    const wallArea = 2 * (L + W) * H;
    const openingArea = openings.reduce((sum, o) => sum + ((parseFloat(o.w) || 0) * (parseFloat(o.h) || 0)), 0);
    const netWallArea = Math.max(0, wallArea - openingArea);
    const ceilingArea = includeCeiling ? L * W : 0;
    const totalDrywallArea = netWallArea + ceilingArea;

    // Studs: 4 walls combined linear feet
    const wallLengthFt = 2 * (L + W);
    const studs = intCeil((wallLengthFt * 12) / s) + 4; // +4 for corners
    const platesLF = wallLengthFt * 3; // top, double-top, bottom

    // Drywall sheets (4×8 = 32 sq ft) with 10% waste
    const drywallSheets = intCeil(totalDrywallArea * 1.1 / 32);

    // Insulation sq ft (wall area, no openings)
    const insulationSqFt = netWallArea;

    // Flooring/subfloor (if we treat L×W as floor too)
    const flooringSqFt = L * W;

    return {
      wallArea: fmt(wallArea, 0),
      openingArea: fmt(openingArea, 0),
      netWallArea: fmt(netWallArea, 0),
      ceilingArea: fmt(ceilingArea, 0),
      totalDrywallArea: fmt(totalDrywallArea, 0),
      studs,
      platesLF: fmt(platesLF, 1),
      drywallSheets,
      insulationSqFt: fmt(insulationSqFt, 0),
      flooringSqFt: fmt(flooringSqFt, 0),
      paintGallons: fmt((totalDrywallArea * 2) / 350, 1),
    };
  }, [length, width, height, spacing, openings, includeCeiling]);

  const updateOpening = (i, key, val) => {
    setOpenings((o) => o.map((row, idx) => idx === i ? { ...row, [key]: val } : row));
  };
  const addOpening = () => setOpenings((o) => [...o, { type: 'window', w: '', h: '' }]);
  const removeOpening = (i) => setOpenings((o) => o.filter((_, idx) => idx !== i));

  const saveCutList = async () => {
    if (!totals) { toast.error('Fill in room dimensions first'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/cut-lists`, {
        project_id: projectId || null,
        length: parseFloat(length) || 0,
        width: parseFloat(width) || 0,
        height: parseFloat(height) || 0,
        spacing: parseFloat(spacing) || 16,
        include_ceiling: includeCeiling,
        openings: openings
          .filter((o) => parseFloat(o.w) && parseFloat(o.h))
          .map((o) => ({ type: o.type, w: parseFloat(o.w), h: parseFloat(o.h) })),
        totals,
      });
      toast.success('Cut list saved');
      await fetchSaved();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeSaved = async (id) => {
    if (!window.confirm('Delete this saved cut list?')) return;
    try {
      await axios.delete(`${API}/cut-lists/${id}`);
      setSavedLists((l) => l.filter((x) => x.id !== id));
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <Field label="Length (ft)" value={length} onChange={setLength} type="number" />
        <Field label="Width (ft)" value={width} onChange={setWidth} type="number" />
        <Field label="Height (ft)" value={height} onChange={setHeight} type="number" />
      </div>
      <Field label="Stud spacing (in OC)" value={spacing} onChange={setSpacing} type="number" />
      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <input type="checkbox" checked={includeCeiling} onChange={(e) => setIncludeCeiling(e.target.checked)} className="rounded" />
        Include ceiling drywall
      </label>

      <div>
        <p className="text-xs font-bold text-gray-600 mb-1">Openings (windows / doors)</p>
        <div className="space-y-2">
          {openings.map((o, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-end">
              <select
                value={o.type}
                onChange={(e) => updateOpening(i, 'type', e.target.value)}
                className="col-span-4 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white"
              >
                <option value="window">Window</option>
                <option value="door">Door</option>
                <option value="other">Other</option>
              </select>
              <input
                type="number" placeholder="W ft" value={o.w}
                onChange={(e) => updateOpening(i, 'w', e.target.value)}
                className="col-span-3 px-2 py-1.5 border border-gray-200 rounded text-xs"
              />
              <input
                type="number" placeholder="H ft" value={o.h}
                onChange={(e) => updateOpening(i, 'h', e.target.value)}
                className="col-span-3 px-2 py-1.5 border border-gray-200 rounded text-xs"
              />
              <button onClick={() => removeOpening(i)} className="col-span-2 text-red-500 text-xs font-bold hover:bg-red-50 py-1.5 rounded">×</button>
            </div>
          ))}
        </div>
        <button onClick={addOpening} className="mt-1 text-xs text-blue-600 font-semibold">+ Add opening</button>
      </div>

      {totals && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1 text-sm">
          <p className="font-bold text-emerald-900 mb-1">Material take-off</p>
          <Row k="Total wall area" v={`${totals.wallArea} sq ft`} />
          <Row k="Less openings" v={`${totals.openingArea} sq ft`} />
          <Row k="Net walls" v={`${totals.netWallArea} sq ft`} />
          {includeCeiling && <Row k="Ceiling" v={`${totals.ceilingArea} sq ft`} />}
          <div className="border-t border-emerald-200 my-2" />
          <Row k="Studs" v={`${totals.studs} pieces`} />
          <Row k="Plates (top + double-top + bottom)" v={`${totals.platesLF} ft`} />
          <Row k="Drywall sheets (4×8, +10% waste)" v={`${totals.drywallSheets} sheets`} />
          <Row k="Insulation" v={`${totals.insulationSqFt} sq ft`} />
          <Row k="Subfloor area" v={`${totals.flooringSqFt} sq ft`} />
          <Row k="Paint (2 coats)" v={`${totals.paintGallons} gal`} />
        </div>
      )}

      <div className="border-t border-gray-200 pt-3 space-y-2">
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
        >
          <option value="">Save without project tag</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={saveCutList} disabled={saving || !totals} className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:bg-gray-200 flex items-center justify-center gap-1.5" data-testid="save-cutlist-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Cut List'}
          </button>
          <button onClick={() => setShowSaved((s) => !s)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-1">
            <FolderOpen className="w-4 h-4" /> {savedLists.length}
          </button>
        </div>
      </div>

      {showSaved && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-600">Saved cut lists</p>
          {savedLists.length === 0 ? (
            <p className="text-xs text-gray-500 italic">None yet.</p>
          ) : savedLists.map((s) => (
            <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-2 flex items-center justify-between text-xs" data-testid={`saved-cutlist-${s.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-bold">{s.project_name || 'No project'} — {s.length}×{s.width}×{s.height}</p>
                <p className="text-gray-500">{s.totals?.studs} studs · {s.totals?.drywallSheets} sheets · {s.totals?.paintGallons} gal paint</p>
              </div>
              <button onClick={() => removeSaved(s.id)} className="text-red-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === Shared field helpers ===

function Select({ label, value, onChange, options = [] }) {
  return (
    <div>
      <label className="text-xs font-bold text-gray-600">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
      >
        {options.map((o) => (
          <option key={String(o.v)} value={o.v}>{o.t}</option>
        ))}
      </select>
    </div>
  );
}


function Field({ label, value, onChange, type = 'text', hint = '' }) {
  return (
    <div>
      <label className="text-xs font-bold text-gray-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
      {hint && <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
  );
}
function Result({ label, value, unit }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center justify-between">
      <span className="text-xs font-bold text-blue-900">{label}</span>
      <span className="text-sm font-bold text-blue-900">{value} <span className="text-[11px] font-semibold text-blue-700">{unit}</span></span>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-emerald-900">{k}</span>
      <span className="font-bold text-emerald-900">{v}</span>
    </div>
  );
}

// === Main page ===

const TABS = [
  { id: 'drywall',  label: 'Drywall',  icon: Layers,       cmp: <DrywallCalc /> },
  { id: 'paint',    label: 'Paint',    icon: Paintbrush,   cmp: <PaintCalc /> },
  { id: 'concrete', label: 'Concrete', icon: Box,          cmp: <ConcreteCalc /> },
  { id: 'lumber',   label: 'Lumber',   icon: TreePine,     cmp: <LumberCalc /> },
  { id: 'tile',     label: 'Tile',     icon: Grid3x3,      cmp: <TileCalc /> },
  { id: 'shingles', label: 'Shingles', icon: HomeIcon,     cmp: <ShingleCalc /> },
  { id: 'deck',     label: 'Deck Take-off', icon: Footprints, cmp: <DeckCalc /> },
];

export default function EstimatorTools() {
  const [tab, setTab] = useState('drywall');
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    axios.get(`${API}/projects`).then((r) => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-28">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-1">
          <Calculator className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Estimator Tools</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">Quick math + a saveable room cut list.</p>

        {/* Tab chips */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1 ${tab === t.id ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
          <button
            onClick={() => setTab('cutlist')}
            className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1 ${tab === 'cutlist' ? 'bg-emerald-600 text-white' : 'bg-white border border-emerald-200 text-emerald-700'}`}
            data-testid="tab-cutlist"
          >
            <Hammer className="w-3.5 h-3.5" /> Room Take-off
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          {tab === 'cutlist' ? <CutListCalc projects={projects} /> : TABS.find((t) => t.id === tab)?.cmp}
        </div>

        <p className="text-[10px] text-gray-400 mt-3 text-center">
          Numbers are estimates. Always verify with your project plans and supplier.
        </p>
      </div>
    </div>
  );
}
